import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import {
  bidvService,
  BidvRefundBank,
  BidvRefundBankFilters,
} from '../services/bidv.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useBidvRefundBanks = (initialFilters: BidvRefundBankFilters = {}) => {
  const [refundBanks, setRefundBanks] = useState<BidvRefundBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<BidvRefundBankFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bidvService.getRefundBanks(filters);
      setRefundBanks(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch BIDV refund banks'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  return { refundBanks, loading, error, meta, filters, setFilters, refetch: fetchBanks };
};

export const useBidvRefundBankDetail = (id: string) => {
  const [refundBank, setRefundBank] = useState<BidvRefundBank | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBank = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await bidvService.getRefundBankById(id);
      setRefundBank(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch refund bank'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBank();
  }, [fetchBank]);

  return { refundBank, loading, error, refetch: fetchBank };
};
